import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { pipelineApi, useActionPipeline, useAffaires } from '../../api/pipeline.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleAffaireDialog, type NouvelleAffaireDialogProps } from './NouvelleAffaireDialog.js';

export function EntreeNouvelleAffaire(
  props: Pick<NouvelleAffaireDialogProps, 'contactInitial' | 'oeuvreInitiale'>,
) {
  const { galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const [open, setOpen] = useState(false);
  if (!galerieAModule(galerie, 'pipeline-ventes') || !galerieALaction(galerie, 'pipeline:write'))
    return null;
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {t('pipeline.nouvelle')}
      </Button>
      {open ? <NouvelleAffaireDialog open onOpenChange={setOpen} {...props} /> : null}
    </>
  );
}
export function EntreeDepuis({
  source,
  id,
  contactId,
}: {
  source: 'demande' | 'offre';
  id: string;
  contactId?: string;
}) {
  const { galerie } = useShellContext();
  if (!galerieAModule(galerie, 'pipeline-ventes') || !galerieALaction(galerie, 'pipeline:write'))
    return null;
  return <Depuis source={source} id={id} contactId={contactId} />;
}
function Depuis({
  source,
  id,
  contactId,
}: {
  source: 'demande' | 'offre';
  id: string;
  contactId?: string;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const navigate = useNavigate();
  const affaires = useAffaires(galerieSlug, { contactId });
  const existante = affaires.data?.affaires.find((a) =>
    source === 'offre' ? a.liens.offreIds?.includes(id) : a.liens.demandeId === id,
  );
  const mutation = useActionPipeline(galerieSlug, (_: void, appel) =>
    pipelineApi.depuis(galerieSlug, source, id, appel),
  );
  const details = estApiError(mutation.error)
    ? (mutation.error.details as { raison?: string; affaireId?: string } | null)
    : null;
  const suivie = existante?.id ?? (details?.raison === 'deja_suivie' ? details.affaireId : null);
  if (suivie)
    return (
      <Link to={`/${galerieSlug}/pipeline?affaire=${encodeURIComponent(suivie)}`}>
        {t('regie:pipeline.voirAffaire')}
      </Link>
    );
  return (
    <div>
      <Button
        variant="secondary"
        loading={mutation.isPending}
        onClick={() =>
          void mutation
            .mutateAsync()
            .then((r) =>
              navigate(`/${galerieSlug}/pipeline?affaire=${encodeURIComponent(r.affaire.id)}`),
            )
            .catch(() => {})
        }
      >
        {t(source === 'offre' ? 'regie:pipeline.suivreOffre' : 'regie:pipeline.creerDepuisDemande')}
      </Button>
      {mutation.isError ? (
        <p role="alert">
          {estApiError(mutation.error) ? mutation.error.message : t('commun:erreurReseau')}
        </p>
      ) : null}
    </div>
  );
}
